/**
 * Architecture career ladder — role profiles with expected skill levels.
 * Titles are MOREgroup's actual ladder: Architectural Associate → Architect
 * → Project Architect → Senior Project Architect → Senior Architect →
 * Associate Principal → Principal → Managing Principal.
 *
 * Each expectation names a template section + row LABEL (not id) and the
 * minimum level expected at that role. Labels are resolved against the live
 * template at read time (see logic/roleFit.resolveExpectation), so a label
 * tweak in the template builder degrades gracefully (unmatched expectations
 * are dropped) instead of silently pointing at the wrong skill.
 * `ladderIntegrity.test.js` fails the build on any label typo here.
 *
 * Levels: extent4/frequency4 = 0-3, proficiency5 = 0-4.
 * HR can edit all of this in Growth → Admin → Career ladder; this file is
 * only the first-run seed.
 */

const t = (label, level) => ({ sectionId: 'technicalSkills', label, level });
const m = (label, level) => ({ sectionId: 'managementSkills', label, level });
const d = (label, level) => ({ sectionId: 'designSkills', label, level });
const doc = (label, level) => ({ sectionId: 'documentationDetail', label, level });
const tool = (label, level) => ({ sectionId: 'tools', label, level });
const soft = (label, level) => ({ sectionId: 'softSkills', label, level });
const phase = (label, level) => ({ sectionId: 'projectPhases', label, level });

export const ARCHITECTURE_ROLES = {
  templateId: 'architecture',
  tracks: [
    {
      id: 'architecture',
      name: 'Architecture',
      roles: [
        {
          id: 'archAssociate',
          title: 'Architectural Associate',
          summary: 'Entry role — learning production under close direction, working toward licensure.',
          titleMatches: ['architectural associate', 'architectural intern', 'designer in training'],
          expectations: [
            tool('Revit modeling', 1),
            tool('Bluebeam Revu', 1),
            t('Construction documentation (plans, details, sections)', 1),
            doc('Floor plans (dimensioned, annotated, code-compliant)', 1),
            doc('Interior elevations', 1),
            t('Interior wall assemblies', 1),
            soft('Asks appropriate clarifying questions before proceeding', 2),
            soft('Works effectively in a team environment', 2),
          ],
        },
        {
          id: 'architect',
          title: 'Architect',
          summary: 'Licensed — owns document packages and coordinates consultants day-to-day.',
          // Plain 'architect' is safe despite being a substring of every other
          // title — matchRoleForTitle picks the LONGEST match, so 'senior
          // project architect' etc. always win over this rung.
          titleMatches: ['architect', 'architect i', 'architect ii', 'staff architect'],
          expectations: [
            tool('Revit modeling', 3),
            tool('Revit documentation (CD production)', 2),
            tool('Revit worksharing', 2),
            tool('Bluebeam Revu', 2),
            t('Construction documentation (plans, details, sections)', 2),
            t('Interior wall assemblies', 2),
            t('Exterior wall assemblies', 2),
            t('Building envelope systems (air/vapor/thermal control)', 2),
            t('IBC / ICC code application and interpretation', 2),
            t('Accessibility (ADA — interior and exterior)', 2),
            t('Life safety / egress design', 2),
            t('MEP coordination', 2),
            t('Redlining and back-checking drawings', 2),
            doc('Floor plans (dimensioned, annotated, code-compliant)', 2),
            doc('Exterior wall sections and details', 2),
            doc('Fire-rated assemblies and penetrations', 2),
            doc('Restroom detailing (ADA/code compliant)', 2),
            phase('Design Development (DD)', 2),
            phase('Construction Documents (CD)', 2),
            m('Breaks deliverables into actionable tasks', 2),
            soft('Identifies issues early and escalates appropriately', 2),
            soft('Communicates clearly and professionally with consultants', 2),
          ],
        },
        {
          id: 'projectArchitect',
          title: 'Project Architect',
          summary: 'Technical lead — signs off packages, runs coordination, mentors production staff.',
          titleMatches: ['project architect'],
          expectations: [
            tool('Revit documentation (CD production)', 3),
            tool('Revit coordination review', 2),
            t('Construction documentation (plans, details, sections)', 4),
            t('IBC / ICC code application and interpretation', 3),
            t('Life safety / egress design', 3),
            t('Fire-rated assemblies and penetrations', 3),
            t('Interdisciplinary coordination leadership', 3),
            t('QA/QC review processes', 3),
            t('Identify and resolve constructability issues', 3),
            t('RFIs', 3),
            t('Submittal and shop drawing review', 3),
            doc('Producing a fully coordinated, construction-ready drawing set', 3),
            doc('Redlining and back-checking for technical accuracy', 3),
            phase('AHJ Coordination / Permitting', 2),
            phase('Construction Administration (CA)', 2),
            m('Coordinates consultants effectively', 3),
            m('Manages RFIs', 3),
            m('Communicates risks, issues, and updates effectively', 3),
            soft('Mentors and supports junior staff', 2),
            soft('Effectively defends design decisions when appropriate', 2),
          ],
        },
        {
          id: 'seniorProjectArchitect',
          title: 'Senior Project Architect',
          summary: 'Leads the hardest technical work; manages schedule, budget, and client day-to-day.',
          titleMatches: ['senior project architect'],
          expectations: [
            t('Construction documentation (plans, details, sections)', 4),
            t('IBC / ICC code application and interpretation', 4),
            t('Lead technical coordination across disciplines', 4),
            t('QA/QC review processes', 4),
            t('Risk identification in design', 3),
            t('Phasing and occupied renovation planning', 3),
            doc('Producing a fully coordinated, construction-ready drawing set', 4),
            m('Develops project schedules', 3),
            m('Manages construction budgets', 2),
            m('Leads client meetings and presentations', 3),
            m('Manages change orders and cost impacts', 3),
            m('Delegates work and distributes workload effectively', 3),
            d('Balances design intent with cost, constructability, and schedule', 3),
            soft('Mentors and supports junior staff', 3),
            soft('Navigates difficult conversations professionally when needed', 3),
          ],
        },
        {
          id: 'seniorArchitect',
          title: 'Senior Architect',
          summary: 'Firm-level technical authority — sets documentation standards, resolves the toughest details, grows the bench.',
          titleMatches: ['senior architect'],
          expectations: [
            t('Construction documentation (plans, details, sections)', 4),
            t('Detailing practices and standards', 4),
            t('Drawing set organization and standards', 4),
            t('QA/QC review processes', 4),
            t('Building envelope systems (air/vapor/thermal control)', 3),
            t('IEBC (existing building code)', 3),
            t('Risk identification in design', 4),
            doc('Waterproofing and flashing details', 3),
            doc('Coordination of details across disciplines', 4),
            doc('Redlining and back-checking for technical accuracy', 4),
            m('Understanding of contracts and project agreements', 3),
            soft('Mentors and supports junior staff', 3),
            soft('Influences or supports others beyond assigned responsibilities', 3),
          ],
        },
        {
          id: 'associatePrincipal',
          title: 'Associate Principal',
          summary: 'Emerging practice leader — client relationships, project financials, and design direction.',
          titleMatches: ['associate principal'],
          expectations: [
            m('Manages client communications', 3),
            m('Leads client meetings and presentations', 3),
            m('Manages internal project budgets', 3),
            m('Manages construction budgets', 3),
            m('Manages staffing needs across the project', 3),
            m('Identifies project risks proactively', 3),
            m('Develops mitigation and recovery plans', 3),
            d('Translates client goals into design solutions', 3),
            d('Guides design decision-making within project teams', 3),
            d('Leads planning sessions with clients and stakeholders', 3),
            soft('Demonstrates leadership potential', 3),
            soft('Builds trust with project teams, consultants, and clients', 3),
            soft('Demonstrates public speaking and presentation skills', 2),
          ],
        },
        {
          id: 'principal',
          title: 'Principal',
          summary: 'Practice leader — owns client accounts, pursuit strategy, staffing, and studio quality.',
          titleMatches: ['principal'],
          expectations: [
            m('Manages client communications', 4),
            m('Leads client meetings and presentations', 4),
            m('Manages internal project budgets', 4),
            m('Manages staffing needs across the project', 4),
            m('Identifies project risks proactively', 4),
            m('Understanding of contracts and project agreements', 3),
            m('Builds and manages vendor and rep relationships', 3),
            d('Ensures design intent is maintained across disciplines and project phases', 3),
            d('Supports project interviews and presentations', 3),
            soft('Mentors and supports junior staff', 3),
            soft('Navigates difficult conversations professionally when needed', 3),
            soft('Demonstrates public speaking and presentation skills', 3),
          ],
        },
        {
          id: 'managingPrincipal',
          title: 'Managing Principal',
          summary: 'Leads the practice — portfolio, people, and firm direction.',
          titleMatches: ['managing principal'],
          expectations: [
            m('Manages client communications', 4),
            m('Manages staffing needs across the project', 4),
            m('Understanding of contracts and project agreements', 4),
            m('Identifies project risks proactively', 4),
            m('Develops mitigation and recovery plans', 4),
            m('Manages change (scope, schedule, budget)', 4),
            d('Leads planning sessions with clients and stakeholders', 4),
            soft('Demonstrates leadership potential', 3),
            soft('Builds trust with project teams, consultants, and clients', 3),
            soft('Adapts to changes in scope, schedule, budget, or client priorities', 3),
          ],
        },
      ],
    },
  ],
};
